/**
 * Consent Request Page
 *
 * React implementation of the consent request management page.
 * Handles listing, filtering, log inspection, downloads, and consent creation.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DrawerProvider } from 'equal-ds-ui';
import {
  ConsentFilters,
  ConsentTable,
  ConsentDrawer,
  CreateConsentDrawer,
  ConsentDownloadDrawer,
  FiDataDrawer,
  AccountDetailsDrawer,
  GenerateQRDrawer,
} from '.';
import { LendingReportDrawer } from './lending-report';
import { lendingReportAPI } from '../../../services/lending-report-api.service';
import { isSmartOrchestrationEnabled } from '../../../utils/feature-flags';
import { generateLendingReportPDF } from '../../../utils/lending-report-pdf';
import { CsvDownloadModal } from './CsvDownloadModal';
import { Toast } from '../../../components/ui/Toast';
import { useErrorHandler } from '../../../hooks/useErrorHandler';
import { envService } from '../../../services/env.service';
import { consentRequestAPI } from '../../../services/consent-request-api.service';
import { downloadAPI } from '../../../services/download-api.service';
import { accountAggregatorAPI } from '../../../services/account-aggregator-api.service';
import { productsAPI } from '../../../services/products-api.service';
import {
  DEFAULT_CURRENT_PAGE,
  DEFAULT_PAGE_SIZE,
} from '../../../constants/pagination.constants';
import type {
  ConsentFiltersState,
  ConsentFilterOptions,
  ConsentRequestRecord,
  ConsentRequestPageProps,
  ConsentTableRow,
  DrawerTab,
  ConsentAccountGroup,
  ConsentAnalyticsOption,
  ConsentSearchDomain,
  ConsentTablePermissions,
  ConsentCreatePayload,
  ConsentCSVRow,
  ConsentAccountRecord,
  ConsentRequestListParams,
  ConsentTimelineAPIResponse,
  TransformedDataSession,
} from '../../../types/consent';
import type { TimelineEvent, ActivityLogTimeline, OrchestrationStatusData } from '../../../types/timeline';
import { transformActivityLogs } from './activity-log-transform';
import type { AggregatorOption, ConsentSuccessData } from './CreateConsentDrawer';
import { getFinalAssistedJourneyFlags } from '../../../utils/assisted-journey';
import type { AssistedJourneyFlags } from '../../../utils/assisted-journey';
import type { ProductRecord } from '../../../services/products-api.service';
import type { StateRecord } from '../../../types/consent';
import { transformConsentTimeline, transformDataSessions } from '../../../utils/timeline-transformers';
import { buildXmlDownloads } from '../../../utils/fi-data-download.utils';

interface UserInfo {
  role?: string;
  menuList?: Array<{
    code?: string;
    features?: Array<{ code?: string | null | undefined }>;
  }>;
  ui_config?: Record<string, any>;
}

interface ConsentFeatureFlags {
  canCreateConsent: boolean;
  canDownloadCsv: boolean;
}

const CONSENT_MENU_CODE = 'CONSENT-REQUEST';

// Static consent status options (known, finite values)
const CONSENT_STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'active', label: 'ACTIVE' },
  { value: 'pending', label: 'PENDING' },
  { value: 'expired', label: 'EXPIRED' },
  { value: 'revoked', label: 'REVOKED' },
  { value: 'rejected', label: 'REJECTED' },
  { value: 'paused', label: 'PAUSED' },
];

const getUserInfo = (): UserInfo | null => {
  try {
    const encoded = sessionStorage.getItem('loggedUserInfo');
    if (!encoded) {
      return null;
    }
    return JSON.parse(atob(encoded));
  } catch (error) {
    console.error('[ConsentRequestPage] Failed to parse user info:', error);
    return null;
  }
};

const derivePermissions = (userInfo: UserInfo | null): {
  permissions: ConsentTablePermissions;
  featureFlags: ConsentFeatureFlags;
} => {
  if (!userInfo?.menuList) {
    return {
      permissions: {
        canViewFiData: true,
        canDownloadFiData: true,
        canViewLogs: true,
        canSendSms: false,
        canTriggerFiRequest: false,
        canViewAccountDetails: true,
        canViewLendingReport: true,
      },
      featureFlags: {
        canCreateConsent: true,
        canDownloadCsv: true,
      },
    };
  }

  const menu = userInfo.menuList.find((item) => item.code === CONSENT_MENU_CODE);
  const features = menu?.features ?? [];
  const hasFeature = (code: string) =>
    features.some((feature) => feature.code?.trim() === code);
  const isSuperAdmin = userInfo.role === 'superadmin';

  return {
    permissions: {
      canViewFiData: hasFeature('CONSENTREQUESTVIEW') || isSuperAdmin,
      canDownloadFiData: hasFeature('CONSENTREQUEST-DOWNLOAD') || isSuperAdmin,
      canViewLogs: hasFeature('CONSENTREQUEST-VIEWLOGS') || isSuperAdmin,
      canSendSms: hasFeature('CONSENTREQUEST-SENDSMS') || isSuperAdmin,
      canTriggerFiRequest: hasFeature('CONSENTREQUEST-FIREQUEST') || isSuperAdmin,
      canViewAccountDetails: hasFeature('CONSENTREQUEST-ACCOUNTDETAILS') || isSuperAdmin,
      canViewLendingReport: hasFeature('LENDINGREPORT-VIEW') || isSuperAdmin,
    },
    featureFlags: {
      canCreateConsent: hasFeature('CREATECONSENTREQUEST') || isSuperAdmin,
      canDownloadCsv: hasFeature('CONSENTREQUESTDOWNLOADCSV') || isSuperAdmin,
    },
  };
};

const buildEmptyFilterOptions = (): ConsentFilterOptions => ({
  templates: [],
  purposeCodes: [],
  statuses: [],
  aggregators: [],
});

const buildInitialFilters = (): ConsentFiltersState => {
  return {
    selectedTemplate: null,
    selectedPurposeCode: null,
    selectedStatus: null,
    selectedAggregator: null,
  };
};

const normalizeString = (value: unknown): string => {
  if (value === null || value === undefined) {
    return '';
  }
  return String(value).toLowerCase().trim();
};

// Note: Client-side filtering removed - server-side filtering is now used
// The applyFilters and buildFilterOptions functions were removed as they are no longer needed

const groupAccountsByFip = (accounts: ConsentAccountRecord[]): ConsentAccountGroup[] => {
  const map = new Map<string, ConsentAccountGroup>();
  accounts.forEach((account) => {
    const fipId = account.fip_id ?? 'UNKNOWN';
    if (!map.has(fipId)) {
      map.set(fipId, { fip_id: fipId, accounts: [] });
    }
    map.get(fipId)?.accounts.push(account);
  });
  return Array.from(map.values());
};

const toTableRow = (record: ConsentRequestRecord, index: number): ConsentTableRow => {
  const rowId =
    record.consent_handle ??
    record.consent_id ??
    record.account_id ??
    `${record.customer_vua ?? 'row'}-${index}`;
  return {
    ...record,
    _rowId: rowId,
  };
};

/**
 * FP-6471 — User Activity Logs visibility (combined gate)
 *
 * The "User Activity Logs" tab is rendered only when BOTH conditions hold:
 *   1. Global flag `isUserActivityLogsEnabled` is true
 *      (set in sessionStorage by the Angular bootstrap from /browser/info,
 *      driven by `config_master.user_activity_logs.enabled` on the backend)
 *   2. The selected consent's product has `feature_config.webrd_wrapper_enabled === true`
 *      (without it the WebRD wrapper isn't engaged for that product, so no events
 *      can be captured even if the global flag is on)
 */
function isGlobalActivityLogsFlagEnabled(): boolean {
  try {
    return sessionStorage.getItem('isUserActivityLogsEnabled') === 'true';
  } catch {
    return false;
  }
}

function consentProductHasWrapper(
  selectedConsent: ConsentRequestRecord | null,
  productOptions: ProductRecord[]
): boolean {
  if (!selectedConsent?.product_id) return false;
  const product = productOptions.find(
    p => p.product_code === selectedConsent.product_id
  );
  return product?.feature_config?.webrd_wrapper_enabled === true;
}

export const ConsentRequestPage: React.FC<ConsentRequestPageProps> = () => {
  const envConfig = useMemo(() => envService.getConfig(), []);
  const userInfo = useMemo(() => getUserInfo(), []);

  const { errorMessage, handleError, clearError } = useErrorHandler();

  const { permissions, featureFlags } = useMemo(
    () => derivePermissions(userInfo),
    [userInfo]
  );

  const [rawRecords, setRawRecords] = useState<ConsentRequestRecord[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [smsEnabled, setSmsEnabled] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [currentPage, setCurrentPage] = useState<number>(DEFAULT_CURRENT_PAGE);
  const [pageSize, setPageSize] = useState<number>(envConfig.pageSize ?? DEFAULT_PAGE_SIZE);
  const [searchState, setSearchState] = useState<{ domain: ConsentSearchDomain; query: string }>({
    domain: 'customer_vua',
    query: '',
  });

  const [filterOptions, setFilterOptions] = useState<ConsentFilterOptions>(buildEmptyFilterOptions());
  const [filters, setFilters] = useState<ConsentFiltersState>(buildInitialFilters());
  const [appliedFilters, setAppliedFilters] = useState<ConsentFiltersState>(buildInitialFilters());

  const hasUnappliedChanges = useMemo(() => {
    return JSON.stringify(filters) !== JSON.stringify(appliedFilters);
  }, [filters, appliedFilters]);

  const hasAnyFiltersSelected = useMemo(() => {
    return (
      filters.selectedTemplate !== null ||
      filters.selectedPurposeCode !== null ||
      filters.selectedStatus !== null ||
      filters.selectedAggregator !== null
    );
  }, [filters]);

  // Server-side filtering is now used - no client-side filtering needed
  // Backend returns filtered results, so we use rawRecords directly
  const tableRows = useMemo<ConsentTableRow[]>(
    () => rawRecords.map(toTableRow),
    [rawRecords]
  );

  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<DrawerTab>('consent');
  const [selectedConsent, setSelectedConsent] = useState<ConsentRequestRecord | null>(null);

  // Timeline state
  const [consentTimelineData, setConsentTimelineData] = useState<TimelineEvent[] | null>(null);
  const [dataSessionsData, setDataSessionsData] = useState<TransformedDataSession[] | null>(null);
  const [timelineMetadata, setTimelineMetadata] = useState<ConsentTimelineAPIResponse['timeline_metadata'] | null>(null);
  const [timelineError, setTimelineError] = useState<string | null>(null);
  const [timelineLoading, setTimelineLoading] = useState<boolean>(false);

  // Activity-log state (FP-6471)
  const [activityLogs, setActivityLogs] = useState<ActivityLogTimeline | null>(null);
  const [activityLogsError, setActivityLogsError] = useState<string | null>(null);
  const [activityLogsLoading, setActivityLogsLoading] = useState<boolean>(false);

  // Orchestration-status state (View Logs → Orchestration tab)
  const [orchestrationData, setOrchestrationData] = useState<OrchestrationStatusData | null>(null);
  const [orchestrationError, setOrchestrationError] = useState<string | null>(null);
  const [orchestrationLoading, setOrchestrationLoading] = useState<boolean>(false);

  // Cache for account details (used by download functionality)
  const accountCacheRef = useRef<Record<string, ConsentAccountGroup[]>>({});

  const [downloadDrawerOpen, setDownloadDrawerOpen] = useState<boolean>(false);
  const [downloadConsent, setDownloadConsent] = useState<ConsentRequestRecord | null>(null);
  const [downloadAccountGroups, setDownloadAccountGroups] = useState<ConsentAccountGroup[]>([]);
  const [downloadLoading, setDownloadLoading] = useState<boolean>(false);

  const [analyticsOptions, setAnalyticsOptions] = useState<ConsentAnalyticsOption[]>([]);
  const analyticsLoadedRef = useRef<boolean>(false);

  const [createDrawerOpen, setCreateDrawerOpen] = useState<boolean>(false);
  const [qrDrawerOpen, setQrDrawerOpen] = useState<boolean>(false);
  const [createLoading, setCreateLoading] = useState<boolean>(false);
  const [aggregatorOptions, setAggregatorOptions] = useState<AggregatorOption[]>([]);
  const [productOptions, setProductOptions] = useState<ProductRecord[]>([]);
  const [productDivisions, setProductDivisions] = useState<string[]>([]);
  const [stateOptions, setStateOptions] = useState<StateRecord[]>([]);
  const [createSuccessMessage, setCreateSuccessMessage] = useState<string | null>(null);
  const [createErrorMessage, setCreateErrorMessage] = useState<string | null>(null);
  const createOptionsLoadedRef = useRef<boolean>(false);

  // FI Data Drawer state
  const [fiDataDrawerOpen, setFiDataDrawerOpen] = useState<boolean>(false);
  const [fiDataConsent, setFiDataConsent] = useState<ConsentRequestRecord | null>(null);

  // Account Details Drawer state
  const [accountDetailsDrawerOpen, setAccountDetailsDrawerOpen] = useState<boolean>(false);
  const [accountDetailsConsent, setAccountDetailsConsent] = useState<ConsentRequestRecord | null>(null);

  // Lending Report Drawer state
  const [lendingReportOpen, setLendingReportOpen] = useState<boolean>(false);
  const [lendingReportConsentId, setLendingReportConsentId] = useState<string>('');
  const [lendingReportConsentHandle, setLendingReportConsentHandle] = useState<string>('');
  const [downloadingReportConsentId, setDownloadingReportConsentId] = useState<string | null>(null);

  // CSV Download Modal state
  const [csvModalOpen, setCsvModalOpen] = useState<boolean>(false);
  const [csvDownloading, setCsvDownloading] = useState<boolean>(false);

  // Consent Success Data (for showing success view in CreateConsentDrawer)
  const [consentSuccessData, setConsentSuccessData] = useState<ConsentSuccessData | null>(null);

  const [globalSuccess, setGlobalSuccess] = useState<string | null>(null);

  const endUserListEnabled = Boolean(envConfig.endUserList);
  const userUiConfig = (userInfo as any)?.ui_config ?? {};
  const allowAdditionalInfo = Boolean(userUiConfig?.CreateConsent?.additionalnfoSec);
  const allowStateSelection = Boolean(userUiConfig?.common?.state_config?.addconsent_form);
  const multiConsentLimit = useMemo(() => {
    const fromSession = sessionStorage.getItem('multiConsentLimit');
    if (fromSession) {
      const parsed = Number(fromSession);
      if (!Number.isNaN(parsed) && parsed > 0) {
        return parsed;
      }
    }
    return 1;
  }, []);
  const allowMultiConsent =
    sessionStorage.getItem('featureFlagMultiConsentV3') === 'true';

  const staticQrEnabled =
    sessionStorage.getItem('isStaticQRFeatureEnabled') === 'true' &&
    featureFlags.canCreateConsent;
  const staticQrFeatureConfig = JSON.parse(sessionStorage.getItem('staticQRFeatureConfig') || '{}');
  const staticQrBaseUrl = staticQrFeatureConfig?.qr_base_url || '';

  // Assisted journey flags for current consent creation (computed from selected products)
  // Global check is done inside getFinalAssistedJourneyFlags()
  const [currentAssistedJourneyFlags, setCurrentAssistedJourneyFlags] = useState<AssistedJourneyFlags | null>(null);

  const loadConsentRequests = useCallback(async () => {
    setIsLoading(true);
    clearError();

    try {
      const payload: ConsentRequestListParams = {
        limit: pageSize,
        pageno: currentPage,
      };

      // Add search query to payload
      const trimmedQuery = searchState.query.trim();
      if (trimmedQuery) {
        switch (searchState.domain) {
          case 'customer_vua':
            payload.customer_vua = trimmedQuery;
            break;
          case 'consent_id':
            payload.consent_id = trimmedQuery;
            break;
          case 'consent_handle':
            payload.consent_handle = trimmedQuery;
            break;
          case 'account_id':
            payload.account_id = trimmedQuery;
            break;
          case 'mobile_no':
            payload.mobile_no = trimmedQuery;
            break;
          case 'consent_status':
            payload.consent_status = trimmedQuery;
            break;
          case 'data_delivered':
            payload.data_delivered = trimmedQuery;
            break;
          case 'workflow_txn_id':
            payload.workflow_txn_id = trimmedQuery;
            break;
        }
      }

      // Add filter params to payload (server-side filtering)
      if (appliedFilters.selectedStatus) {
        // Backend expects uppercase status values
        payload.status = appliedFilters.selectedStatus.toUpperCase();
      }
      if (appliedFilters.selectedTemplate) {
        payload.product_id = appliedFilters.selectedTemplate;
      }
      if (appliedFilters.selectedPurposeCode) {
        payload.purpose_code = appliedFilters.selectedPurposeCode;
      }
      if (appliedFilters.selectedAggregator) {
        payload.aa_id = appliedFilters.selectedAggregator;
      }

      const response = await consentRequestAPI.getConsentRequests(payload);
      const rows = response.data.rows || [];
      setRawRecords(rows);
      setTotalCount(response.data.count ?? rows.length);
      setSmsEnabled(Boolean(response.data.SmsNotificationStatus));
    } catch (error) {
      handleError(error, { component: 'ConsentRequestPage', action: 'fetchConsentRequests' });
      setRawRecords([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [pageSize, currentPage, searchState, appliedFilters, handleError, clearError]);

  useEffect(() => {
    void loadConsentRequests();
  }, [loadConsentRequests]);

  // Fetch master data for filter dropdowns on page load
  useEffect(() => {
    const loadMasterData = async () => {
      try {
        const [productsRes, purposesRes, aggregatorsRes] = await Promise.all([
          consentRequestAPI.getProductsList(),
          consentRequestAPI.getPurposesList(),
          consentRequestAPI.getAggregatorsList(),
        ]);

        // Extract rows from nested response structure: { data: { rows: [...] } }
        const productsRows = productsRes?.data?.rows || [];
        const purposesRows = purposesRes?.data?.rows || [];
        const aggregatorsRows = aggregatorsRes?.data?.rows || [];

        const templates = productsRows.map((p) => ({
          value: normalizeString(p.product_code),
          label: p.product_name || p.product_code,
        }));

        const purposeCodes = purposesRows.map((p) => ({
          value: normalizeString(p.purpose_code),
          label: p.purpose_text && p.purpose_code
            ? `${p.purpose_text} (${p.purpose_code})`
            : p.purpose_text || p.purpose_code,
        }));

        const aggregators = aggregatorsRows.map((a) => ({
          value: normalizeString(a.account_aggregator_id),
          label: a.account_aggregator_name || a.account_aggregator_id,
        }));

        setFilterOptions({
          templates,
          purposeCodes,
          statuses: CONSENT_STATUS_OPTIONS,
          aggregators,
        });
      } catch (error) {
        console.warn('[ConsentRequestPage] Failed to load master data for filters:', error);
        // Fallback to static statuses at minimum
        setFilterOptions((prev) => ({
          ...prev,
          statuses: CONSENT_STATUS_OPTIONS,
        }));
      }
    };

    void loadMasterData();
  }, []);

  const handleFiltersChange = useCallback((changes: Partial<ConsentFiltersState>) => {
    setFilters((prev) => ({
      ...prev,
      ...changes,
    }));
  }, []);

  const handleApplyFilters = useCallback(() => {
    setAppliedFilters(filters);
    setCurrentPage(DEFAULT_CURRENT_PAGE); // Reset to page 1 when filters are applied
  }, [filters]);

  const handleResetFilters = useCallback(() => {
    const initial = buildInitialFilters();
    setFilters(initial);
    setAppliedFilters(initial);
    setCurrentPage(DEFAULT_CURRENT_PAGE); // Reset to page 1 when filters are reset
  }, []);

  const handleSearch = useCallback((domain: ConsentSearchDomain, query: string) => {
    setCurrentPage(DEFAULT_CURRENT_PAGE);
    setSearchState({ domain, query });
  }, []);

  const ensureAccountDetails = useCallback(
    async (consent: ConsentRequestRecord, forceReload = false): Promise<ConsentAccountGroup[]> => {
      const cacheKey = consent.consent_id ?? consent.consent_handle ?? '';
      if (!forceReload && cacheKey && accountCacheRef.current[cacheKey]) {
        return accountCacheRef.current[cacheKey];
      }

      const consentId = consent.consent_id;
      if (!consentId) {
        return [];
      }

      try {
        const response = await consentRequestAPI.getConsentAccountDetails({ consentId });
        const rows = response.data.artefactAccounts?.rows ?? [];
        const groups = groupAccountsByFip(rows);
        if (cacheKey) {
          accountCacheRef.current[cacheKey] = groups;
        }
        return groups;
      } catch (error) {
        handleError(error, { component: 'ConsentRequestPage', action: 'fetchAccountDetails' });
        return [];
      }
    },
    [handleError]
  );

  const ensureAnalyticsOptions = useCallback(async () => {
    if (analyticsLoadedRef.current) {
      return;
    }
    try {
      const response = await consentRequestAPI.getAnalyticsOptions();
      setAnalyticsOptions(response.data ?? []);
      analyticsLoadedRef.current = true;
    } catch (error) {
      console.warn('[ConsentRequestPage] Unable to load analytics options', error);
    }
  }, []);

  const handleViewLogs = useCallback(
    async (row: ConsentRequestRecord, tab: DrawerTab) => {
      setSelectedConsent(row);
      // FP-6471: if caller asked for the activity tab but it would be hidden
      // (global flag off OR product wrapper off), fall back to the consent tab.
      const willShowActivityTab =
        isGlobalActivityLogsFlagEnabled() &&
        consentProductHasWrapper(row, productOptions);
      const resolvedTab: DrawerTab =
        tab === 'activity' && !willShowActivityTab ? 'consent' : tab;
      setActiveTab(resolvedTab);
      setDrawerOpen(true);

      // Reset timeline state
      setConsentTimelineData(null);
      setDataSessionsData(null);
      setTimelineMetadata(null);
      setTimelineError(null);
      setTimelineLoading(true); // Start loading

      // Reset activity-log state (FP-6471)
      setActivityLogs(null);
      setActivityLogsError(null);
      setActivityLogsLoading(true);

      // Reset orchestration state; the actual fetch is lazy (Orchestration tab effect)
      setOrchestrationData(null);
      setOrchestrationError(null);
      setOrchestrationLoading(false);

      if (!row.consent_handle) {
        setTimelineError('Invalid consent handle');
        setTimelineLoading(false);
        setActivityLogsError('Invalid consent handle');
        setActivityLogsLoading(false);
        return;
      }

      // FP-6471: activity-log fetch is handled by a separate useEffect below
      // (watches drawerOpen + selectedConsent + productOptions). This avoids a
      // race where productOptions hasn't loaded by the time the user clicks
      // View Logs — the effect re-fires once products arrive.

      try {
        // Call consolidated timeline API
        const response = await consentRequestAPI.getTimelineV2({
          consent_handle: row.consent_handle,
        });

        // Transform API data using transformers
        const consentTimeline = transformConsentTimeline(response.consent_timeline);
        const dataSessions = transformDataSessions(response.data_timeline.sessions);

        // Store transformed data
        setConsentTimelineData(consentTimeline);
        setDataSessionsData(dataSessions);
        setTimelineMetadata(response.timeline_metadata);

      } catch (error) {
        console.error('[ConsentRequestPage] Timeline API Error:', error);
        handleError(error, { component: 'ConsentRequestPage', action: 'fetchTimeline' });

        // Set error state - drawer will show error message
        setTimelineError('Failed to load timeline data. Please try again.');
        setConsentTimelineData(null);
        setDataSessionsData(null);
        setTimelineMetadata(null);
      } finally {
        setTimelineLoading(false); // Stop loading
      }
    },
    [handleError]
  );

  const handleRefreshConsent = useCallback(
    async (row: ConsentRequestRecord) => {
      if (!row.consent_handle) {
        return;
      }
      try {
        await consentRequestAPI.refreshConsent({ ConsentHandle: [row.consent_handle] });
        setGlobalSuccess(`Latest consent details fetched for ${row.customer_vua ?? row.account_id ?? ''}`);
        void loadConsentRequests();
      } catch (error) {
        handleError(error, { component: 'ConsentRequestPage', action: 'refreshConsent' });
      }
    },
    [handleError, loadConsentRequests]
  );

  const handleResendSms = useCallback(
    async (row: ConsentRequestRecord) => {
      if (!row.consent_handle) {
        return;
      }
      try {
        await consentRequestAPI.resendSms({ consenthandle: row.consent_handle });
        setGlobalSuccess('SMS notification has been queued successfully.');
        void loadConsentRequests();
      } catch (error) {
        handleError(error, { component: 'ConsentRequestPage', action: 'resendSms' });
      }
    },
    [handleError, loadConsentRequests]
  );

  const handleTriggerFiRequest = useCallback(
    async (row: ConsentRequestRecord) => {
      if (!row.consent_id) {
        return;
      }
      try {
        await consentRequestAPI.triggerFiRequest({ consentID: row.consent_id });
        setGlobalSuccess('FI request placed successfully.');
      } catch (error) {
        handleError(error, { component: 'ConsentRequestPage', action: 'triggerFiRequest' });
      }
    },
    [handleError]
  );

  const handleOpenDownload = useCallback(
    async (row: ConsentRequestRecord) => {
      setDownloadConsent(row);
      const groups = await ensureAccountDetails(row);
      setDownloadAccountGroups(groups);
      await ensureAnalyticsOptions();
      setDownloadDrawerOpen(true);
    },
    [ensureAccountDetails, ensureAnalyticsOptions]
  );

  const handleViewFiData = useCallback(
    async (row: ConsentRequestRecord) => {
      await ensureAnalyticsOptions();
      setFiDataConsent(row);
      setFiDataDrawerOpen(true);
    },
    [ensureAnalyticsOptions]
  );

  const handleViewAccountDetails = useCallback((row: ConsentRequestRecord) => {
    setAccountDetailsConsent(row);
    setAccountDetailsDrawerOpen(true);
  }, []);

  const handleViewLendingReport = useCallback((row: ConsentRequestRecord) => {
    const consentId = row.consent_id ?? '';
    if (!consentId) return;
    setLendingReportConsentId(consentId);
    setLendingReportConsentHandle(row.consent_handle ?? '');
    setLendingReportOpen(true);
  }, []);

  const handleDownloadLendingReport = useCallback(async (row: ConsentRequestRecord) => {
    const consentId = row.consent_id ?? '';
    if (!consentId) return;
    setDownloadingReportConsentId(consentId);
    try {
      const response = await lendingReportAPI.getReport(consentId);
      if (response.data) {
        generateLendingReportPDF(response.data);
      }
    } catch (err) {
      handleError(err, { component: 'ConsentRequestPage', action: 'downloadLendingReport' });
    } finally {
      setDownloadingReportConsentId(null);
    }
  }, [handleError]);

const downloadTextFile = (content: string, filename: string, mimeType: string) => {
  const blob = new Blob([content], { type: mimeType });
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  window.URL.revokeObjectURL(link.href);
};

const downloadJsonFile = (data: unknown, filename: string) => {
  downloadTextFile(JSON.stringify(data, null, 2), filename, 'application/json');
};

const downloadBlob = (blob: Blob, filename: string) => {
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  window.URL.revokeObjectURL(link.href);
};

const downloadCsv = (rows: ConsentCSVRow[], filename: string) => {
  if (!rows || rows.length === 0) {
    downloadTextFile('No data available', filename, 'text/csv');
    return;
  }
  const headers = Object.keys(rows[0]);
  const csv = [
    headers.join(','),
    ...rows.map((row) =>
      headers
        .map((header) => {
          const value = row[header];
          if (value === null || value === undefined) {
            return '';
          }
          const stringValue = String(value).replace(/"/g, '""');
          return `"${stringValue}"`;
        })
        .join(',')
    ),
  ].join('\r\n');
  downloadTextFile(csv, filename, 'text/csv');
};

const getConsentHandleStatus = (row: any): string =>
  row?.consent_handle == null ? 'FAILED' : (row?.status ?? '');

const getConsentPurposeText = (row: any): string => {
  if (!row?.purpose_text) {
    return '';
  }
  const parts = String(row.purpose_text).split('^#^');
  return [parts[0] ?? '', parts[1] ?? '', parts[2] ?? ''].join(' ').trim();
};

const getConsentFetchDataStatus = (row: any): string => {
  const status = row?.status;
  if (
    status === 'ACTIVE' ||
    status === 'PAUSED' ||
    status === 'REVOKED' ||
    status === 'EXPIRED'
  ) {
    const dataRequests = row?.consent_data_requests ?? [];
    if (dataRequests.length > 0) {
      if (dataRequests[0]?.status === 'COMPLETED') {
        return 'Delivered';
      }
    } else if (row?.consent_id) {
      return 'Failed';
    }
  }
  return '';
};

/**
 * Normalises the user_profile association into a single object regardless of
 * the backend response shape. The consent/download/list endpoint returns the
 * profile either as a nested object (`row.user_profile.emp_id`, the full /
 * non-optimised query path) OR as flat dotted keys (`row['user_profile.emp_id']`,
 * the optimised query path which uses Sequelize `raw: true`). Returns null only
 * when there is no joined profile at all (so callers can render 'N.A.').
 */
const getConsentUserProfile = (
  row: any
): { name: any; emp_id: any; dept_code: any; division: any } | null => {
  if (row?.user_profile && typeof row.user_profile === 'object') {
    return row.user_profile;
  }
  const fields = ['name', 'emp_id', 'dept_code', 'division'];
  const hasFlatKeys = fields.some((field) => `user_profile.${field}` in row);
  if (hasFlatKeys) {
    return {
      name: row['user_profile.name'],
      emp_id: row['user_profile.emp_id'],
      dept_code: row['user_profile.dept_code'],
      division: row['user_profile.division'],
    };
  }
  return null;
};

/**
 * Flattens raw consent rows into flat CSV rows with explicit, human-readable
 * columns. The API returns the user_profile association either nested
 * (`user_profile.emp_id`) or as flat dotted keys depending on the
 * optimised_consent_download_query backend flag, so dumping raw rows via
 * Object.keys drops those fields (emp_id, name, dept_code, division) and
 * serializes nested objects as "[object Object]". This mirrors the legacy
 * Angular `downloadCSV` column mapping so every field — including Employee Id —
 * appears as its own column for both response shapes.
 */
const buildConsentCsvRows = (rows: any[], showWorkflowTxnId: boolean): ConsentCSVRow[] =>
  rows.map((row) => {
    const userProfile = getConsentUserProfile(row);
    return {
    'Customer VUA': row.customer_vua,
    'Consent Handle': row.consent_handle,
    Name: userProfile ? userProfile.name : 'N.A.',
    'Employee Id': userProfile ? userProfile.emp_id : 'N.A.',
    'Business Department Code': userProfile ? userProfile.dept_code : 'N.A.',
    'Department/Division Name': userProfile ? userProfile.division : 'N.A.',
    Status: getConsentHandleStatus(row),
    'Consent Id': row.consent_id === null ? 'N.A.' : row.consent_id,
    'Account/Application Id': row.account_id,
    'Customer Identifier Value': row.mobile_no,
    'Created On': row.createdAt,
    'Fi Request': row.fi_fetch,
    'Consent Start': row.consent_start,
    'Consent Expire': row.consent_expiry,
    'Consent Mode': row.consent_mode,
    'Consent Template Code': row.product_id,
    'Consent Type': row.consent_types,
    'Consent Use': '',
    'Last Used Date/Time': '',
    'Data Consumer Id': '',
    'Data Life Unit': row.data_life_unit,
    'Default Life Value': row.data_life_value,
    'Fetch Type': row.fetch_type,
    'Fi Data Range from': row.fi_data_range_from,
    'Fi Data Range to': row.fi_data_range_to,
    'Fi Types': row.fi_types,
    'Frequency Unit': row.frequency_unit,
    'Frequency Value': row.frequency_value,
    'Purpose Category Type': row.purpose_category_type,
    'Purpose Code': row.purpose_code,
    'Purpose Ref URL': row.purpose_refuri,
    'Purpose Text': getConsentPurposeText(row),
    'Transaction Id': row.txn_id,
    ...(showWorkflowTxnId ? { 'Workflow Transaction Id': row.workflow_txn_id } : {}),
    'Data Provider': row.aa_id,
    'Fetch Data Status': getConsentFetchDataStatus(row),
    };
  });

  const handleDownloadFiData = useCallback(
    async (format: 'JSON' | 'PDF' | 'XML') => {
      if (!downloadConsent) {
        return;
      }
      setDownloadLoading(true);
      try {
        const consentId = downloadConsent.consent_id ?? '';
        if (format === 'JSON') {
          const response = await consentRequestAPI.downloadFiDataJson({
            consentID: consentId,
            downloadjson: true,
          });
          downloadJsonFile(response.data ?? response, `FI-Statement-${consentId}.json`);
        } else if (format === 'PDF') {
          const linkRefNumbers = downloadAccountGroups.flatMap((group) =>
            group.accounts
              .map((account) => account.link_reference_number)
              .filter(Boolean) as string[]
          );
          const response = await consentRequestAPI.downloadFiDataPdf({
            consentID: consentId,
            linkRefNumber: linkRefNumbers,
          });
          downloadBlob(response, `FI-Statement-${consentId}.pdf`);
        } else if (format === 'XML') {
          const response = await consentRequestAPI.downloadFiDataXml({
            consentID: consentId,
          });

          // Check config for account-wise download (matching Angular behavior)
          const downloadAccountWiseXml = Boolean(userUiConfig?.download_account_wise_xml);
          const accountId = downloadConsent?.account_id || consentId;
          const result = buildXmlDownloads(response, downloadAccountWiseXml, accountId);

          result.downloads.forEach((item) => {
            downloadTextFile(item.content, item.filename, 'application/xml');
          });

          // Show warning if any accounts were skipped or data was empty
          if (result.warning) {
            setGlobalSuccess(`Warning: ${result.warning}`);
          }
        }
      } catch (error) {
        handleError(error, { component: 'ConsentRequestPage', action: 'downloadFiData' });
      } finally {
        setDownloadLoading(false);
      }
    },
    [downloadConsent, downloadAccountGroups, handleError]
  );

  const handleDownloadAnalytics = useCallback(
    async (option: ConsentAnalyticsOption, format: 'JSON' | 'EXCEL', linkRefNumber: string) => {
      if (!downloadConsent?.consent_id && !fiDataConsent?.consent_id) {
        return;
      }
      const consentId = downloadConsent?.consent_id ?? fiDataConsent?.consent_id ?? '';
      setDownloadLoading(true);
      try {
        const response = await consentRequestAPI.downloadAnalytics({
          consentID: consentId,
          linkRefNumber: linkRefNumber,
          analyticsType: option.name,
          analyticsVersion: option.version,
          analyticsFormat: format,
        });
        if (format === 'JSON') {
          downloadJsonFile(response.data ?? response, `Analytics-${option.name}-${consentId}.json`);
        } else {
          downloadBlob(
            response,
            `Analytics-${option.name}-${consentId}.${format === 'EXCEL' ? 'xlsx' : 'bin'}`
          );
        }
      } catch (error) {
        handleError(error, { component: 'ConsentRequestPage', action: 'downloadAnalytics' });
      } finally {
        setDownloadLoading(false);
      }
    },
    [downloadConsent, fiDataConsent, handleError]
  );

  const loadCreateConsentOptions = useCallback(async () => {
    if (createOptionsLoadedRef.current) {
      return;
    }
    try {
      const [aggregatorResponse, productsResponse, statesResponse] = await Promise.all([
        accountAggregatorAPI.listAllAggregators(),
        productsAPI.getProducts(''),
        consentRequestAPI.getStates(),
      ]);

      const aggregators = (aggregatorResponse.data.rows ?? [])
        .filter((aggregator) => {
          const status = normalizeString(aggregator.status ? String(aggregator.status) : 'ACTIVE');
          const channelStatus = normalizeString(
            aggregator.channel_status ? String(aggregator.channel_status) : 'ACTIVE'
          );
          return status === 'active' && channelStatus !== 'inactive';
        })
        .map((aggregator) => ({
          value: aggregator.aa_handle,
          label: aggregator.account_aggregator_name ?? aggregator.aa_handle,
          logoUrl: aggregator.logo_url ?? undefined,
        }));

      const products = (productsResponse.data.rows ?? []).filter(
        (product) => normalizeString(product.status) === 'active'
      );

      const divisions = Array.from(
        new Set(
          products
            .map((product) => product.product_division)
            .filter((division): division is string => Boolean(division))
        )
      );

      const states = statesResponse.data.rows ?? [];

      setAggregatorOptions(aggregators);
      setProductOptions(products);
      setProductDivisions(divisions);
      setStateOptions(states);
      createOptionsLoadedRef.current = true;
    } catch (error) {
      handleError(error, { component: 'ConsentRequestPage', action: 'loadCreateConsentOptions' });
    }
  }, [handleError]);

  // FP-6471: hydrate productOptions on mount so the User Activity Logs tab gate
  // can resolve `webrd_wrapper_enabled` without waiting for the Create Consent
  // drawer to be opened. loadCreateConsentOptions is ref-guarded so this is a
  // no-op if it has already run.
  useEffect(() => {
    void loadCreateConsentOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // FP-6471: fetch activity logs whenever the gate inputs are all ready.
  // Running this in an effect (rather than handleViewLogs) ensures we re-fire
  // when productOptions arrives after the user has already clicked View Logs.
  useEffect(() => {
    if (!drawerOpen) return;
    const consent_handle = selectedConsent?.consent_handle;
    if (!consent_handle) return;
    const gateOk =
      isGlobalActivityLogsFlagEnabled() &&
      consentProductHasWrapper(selectedConsent, productOptions);
    if (!gateOk) return;

    let cancelled = false;
    setActivityLogs(null);
    setActivityLogsError(null);
    setActivityLogsLoading(true);
    (async () => {
      try {
        const res = await consentRequestAPI.getActivityLogs({ consent_handle });
        if (cancelled) return;
        setActivityLogs(transformActivityLogs(res.data));
      } catch (err) {
        if (cancelled) return;
        handleError(err, { component: 'ConsentRequestPage', action: 'fetchActivityLogs' });
        setActivityLogs(null);
        setActivityLogsError('Failed to load activity logs. Please try again.');
      } finally {
        if (!cancelled) setActivityLogsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerOpen, selectedConsent, productOptions]);

  // Lazily fetch orchestration status only when the Orchestration tab is open.
  // Hits an external BFF, so we avoid firing it on every View Logs open.
  useEffect(() => {
    if (!drawerOpen || activeTab !== 'orchestration') return;
    const workflow_txn_id = selectedConsent?.workflow_txn_id;
    if (!isSmartOrchestrationEnabled() || !workflow_txn_id) return;

    let cancelled = false;
    setOrchestrationData(null);
    setOrchestrationError(null);
    setOrchestrationLoading(true);
    (async () => {
      try {
        const res = await consentRequestAPI.getOrchestrationStatus({ workflow_txn_id });
        if (cancelled) return;
        setOrchestrationData(res);
      } catch (err) {
        if (cancelled) return;
        handleError(err, { component: 'ConsentRequestPage', action: 'fetchOrchestrationStatus' });
        setOrchestrationData(null);
        setOrchestrationError('Failed to load orchestration status. Please try again.');
      } finally {
        if (!cancelled) setOrchestrationLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerOpen, activeTab, selectedConsent]);

  const handleOpenCreateDrawer = useCallback(async () => {
    setCreateSuccessMessage(null);
    setCreateErrorMessage(null);
    await loadCreateConsentOptions();
    setCreateDrawerOpen(true);
  }, [loadCreateConsentOptions]);

  const handleOpenQRDrawer = useCallback(async () => {
    await loadCreateConsentOptions();
    setQrDrawerOpen(true);
  }, [loadCreateConsentOptions]);

  const handleFetchEndUserDetails = useCallback(
    async (mobileNumber: string) => {
      if (!endUserListEnabled) {
        return null;
      }
      try {
        const response = await consentRequestAPI.getEndUserDetails({ mobileNumber });
        return response.data?.rawData as any;
      } catch (error) {
        handleError(error, { component: 'ConsentRequestPage', action: 'fetchEndUserDetails' });
        return null;
      }
    },
    [endUserListEnabled, handleError]
  );

  const handleCreateConsentSubmit = useCallback(
    async (payload: ConsentCreatePayload) => {
      setCreateLoading(true);
      setCreateSuccessMessage(null);
      setCreateErrorMessage(null);
      try {
        // Compute assisted journey flags based on selected products
        const selectedProductCodes = payload.productIDList ?? (payload.productID ? [payload.productID] : []);
        const computedFlags = getFinalAssistedJourneyFlags(selectedProductCodes, productOptions);
        setCurrentAssistedJourneyFlags(computedFlags);

        let response;
        if (endUserListEnabled) {
          response = await consentRequestAPI.createConsentForEndUser({
            ...payload,
            request_type: 'V3',
          });
        } else if (payload.accountAggregator === '@onemoney') {
          response = await consentRequestAPI.createConsentV2(payload);
        } else {
          response = await consentRequestAPI.createConsent(payload);
        }

        const message =
          response.message ??
          (payload.accountAggregator === '@onemoney'
            ? 'Consent requested successfully. Please advise the customer to approve the request on Onemoney.'
            : 'Consent requested successfully.');

        setCreateSuccessMessage(message);
        setGlobalSuccess(message);
        void loadConsentRequests();

        const redirectionUrl = response.data?.webRedirectionUrl;

        if (redirectionUrl) {
          if (computedFlags.enabled) {
            // Show success view with QR code and sharing options in the same drawer
            setConsentSuccessData({
              consentLink: redirectionUrl,
              // Single-consent returns consent_handle; multi-consent returns consent_handles[].
              consentHandle:
                response.data?.consent_handle || response.data?.consent_handles?.[0] || '',
              mobileNumber: payload.partyIdentifierValue || '',
            });
            // Keep the drawer open - it will show the success view
          } else {
            // Use old redirect behavior when assisted journey is disabled
            setCreateDrawerOpen(false);
            if (envConfig.redirectionNewTab) {
              const win = window.open('', '_blank');
              if (win) {
                setTimeout(() => {
                  win.location.href = redirectionUrl;
                }, 1500);
              }
            } else if (envConfig.redirection) {
              window.location.href = redirectionUrl;
            }
          }
        } else {
          // No redirection URL - just close create drawer
          setCreateDrawerOpen(false);
        }
      } catch (error) {
        setCreateErrorMessage('Failed to create consent request. Please try again.');
        handleError(error, { component: 'ConsentRequestPage', action: 'createConsent' });
      } finally {
        setCreateLoading(false);
      }
    },
    [productOptions, endUserListEnabled, envConfig.redirection, envConfig.redirectionNewTab, handleError, loadConsentRequests]
  );

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setCurrentPage(DEFAULT_CURRENT_PAGE);
  }, []);

  /**
   * Reset consent success data when closing the success view
   */
  const handleResetConsentSuccess = useCallback(() => {
    setConsentSuccessData(null);
    setCreateSuccessMessage(null);
    setCurrentAssistedJourneyFlags(null);
  }, []);

  const isJobBasedDownloadEnabled = sessionStorage.getItem('isJobBasedDownloadEnabled') === 'true';

  const handleCsvDownload = useCallback(
    async (startDate: Date, endDate: Date) => {
      const jobBasedEnabled = sessionStorage.getItem('isJobBasedDownloadEnabled') === 'true';
      setCsvDownloading(true);
      try {
        // Fix: Format dates to YYYY-MM-DD without timezone conversion
        // Setting to noon avoids timezone shift issues when converting to ISO string
        const startDateFixed = new Date(startDate);
        startDateFixed.setHours(12, 0, 0, 0);
        const endDateFixed = new Date(endDate);
        endDateFixed.setHours(12, 0, 0, 0);

        const fromDate = startDateFixed.toISOString().split('T')[0];
        const toDate = endDateFixed.toISOString().split('T')[0];

        if (jobBasedEnabled) {
          await downloadAPI.createJob({
            download_type: 'CONSENT_LIST',
            file_type: 'EXCEL',
            filters: { from: fromDate, to: toDate },
          });
          setCsvModalOpen(false);
          setGlobalSuccess('Report queued! Check the Download Centre');
        } else {
          const response = await consentRequestAPI.getConsentRequestsByDate({
            from: fromDate,
            to: toDate,
          });

          downloadCsv(buildConsentCsvRows(response.data.rows ?? [], isSmartOrchestrationEnabled()), 'consent-requests.csv');
          setCsvModalOpen(false);
          setGlobalSuccess('CSV downloaded successfully');
        }
      } catch (error) {
        handleError(error, { component: 'ConsentRequestPage', action: 'downloadCsv' });
      } finally {
        setCsvDownloading(false);
      }
    },
    [handleError]
  );


  return (
    <DrawerProvider side="right" open={drawerOpen} onOpenChange={setDrawerOpen}>
      <div className="h-full flex flex-col p-4 space-y-4">
        <div>
          <h1 className="text-2xl font-semibold text-text-primary tracking-wide">
            Consent Management
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            Monitor consent requests, review logs, download FI information, and initiate new requests.
          </p>
        </div>

        {/* Fixed position toasts for messages - visible regardless of scroll position */}
        {errorMessage ? (
          <Toast
            message={errorMessage}
            variant="error"
            duration={5000}
            onClose={clearError}
          />
        ) : null}

        {globalSuccess ? (
          <Toast
            message={globalSuccess}
            variant="success"
            duration={4000}
            onClose={() => setGlobalSuccess(null)}
          />
        ) : null}

        <ConsentFilters
          filterOptions={filterOptions}
          selectedFilters={filters}
          onFiltersChange={handleFiltersChange}
          onApplyFilters={handleApplyFilters}
          onResetFilters={handleResetFilters}
          hasUnappliedChanges={hasUnappliedChanges}
          hasAnyFiltersSelected={hasAnyFiltersSelected}
        />

        <ConsentTable
          data={tableRows}
          currentPage={currentPage}
          pageSize={pageSize}
          totalItems={totalCount}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onViewLogs={handleViewLogs}
          onRefreshRow={handleRefreshConsent}
          onRefreshPage={loadConsentRequests}
          onOpenDownloadOptions={handleOpenDownload}
          onViewFiData={permissions.canViewFiData ? handleViewFiData : undefined}
          onViewAccountDetails={permissions.canViewAccountDetails ? handleViewAccountDetails : undefined}
          onTriggerFiRequest={permissions.canTriggerFiRequest ? handleTriggerFiRequest : undefined}
          onSendSms={permissions.canSendSms ? handleResendSms : undefined}
          onSearch={handleSearch}
          currentSearch={searchState}
          isLoading={isLoading}
          permissions={permissions}
          smsEnabled={smsEnabled}
          onCreateConsent={featureFlags.canCreateConsent ? handleOpenCreateDrawer : undefined}
          onGenerateQR={staticQrEnabled && staticQrBaseUrl ? handleOpenQRDrawer : undefined}
          onDownloadCsv={
            featureFlags.canDownloadCsv
              ? () => setCsvModalOpen(true)
              : undefined
          }
          onViewLendingReport={permissions.canViewLendingReport ? handleViewLendingReport : undefined}
          onDownloadLendingReport={permissions.canViewLendingReport ? handleDownloadLendingReport : undefined}
          downloadingReportConsentId={downloadingReportConsentId}
        />
      </div>

      <ConsentDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        selectedConsent={selectedConsent}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        consentTimeline={consentTimelineData}
        dataSessions={dataSessionsData}
        metadata={timelineMetadata}
        error={timelineError}
        isLoading={timelineLoading}
        activityLogs={activityLogs}
        activityLogsError={activityLogsError}
        activityLogsLoading={activityLogsLoading}
        showActivityTab={
          isGlobalActivityLogsFlagEnabled() &&
          consentProductHasWrapper(selectedConsent, productOptions)
        }
        orchestrationData={orchestrationData}
        orchestrationError={orchestrationError}
        orchestrationLoading={orchestrationLoading}
        showOrchestrationTab={
          isSmartOrchestrationEnabled() && !!selectedConsent?.workflow_txn_id
        }
      />

      <DrawerProvider
        side="right"
        open={downloadDrawerOpen}
        onOpenChange={setDownloadDrawerOpen}
      >
        <ConsentDownloadDrawer
          isOpen={downloadDrawerOpen}
          onClose={() => setDownloadDrawerOpen(false)}
          consent={downloadConsent}
          accountGroups={downloadAccountGroups}
          analyticsOptions={analyticsOptions}
          isDownloading={downloadLoading}
          onDownloadFiData={handleDownloadFiData}
          onDownloadAnalytics={handleDownloadAnalytics}
        />
      </DrawerProvider>

      <DrawerProvider
        side="right"
        open={createDrawerOpen}
        onOpenChange={setCreateDrawerOpen}
      >
        <CreateConsentDrawer
          isOpen={createDrawerOpen}
          onClose={() => {
            setCreateDrawerOpen(false);
            handleResetConsentSuccess();
          }}
          onSubmit={handleCreateConsentSubmit}
          isSubmitting={createLoading}
          aggregators={aggregatorOptions}
          products={productOptions}
          productDivisions={productDivisions}
          states={stateOptions}
          multiConsentLimit={multiConsentLimit}
          allowMultiConsent={allowMultiConsent}
          allowAdditionalInfo={allowAdditionalInfo}
          allowStateSelection={allowStateSelection}
          endUserListEnabled={endUserListEnabled}
          defaultAggregator={aggregatorOptions.find((agg) => agg.value === '@onemoney')?.value}
          onFetchEndUserDetails={endUserListEnabled ? handleFetchEndUserDetails : undefined}
          onGenerateAccountId={() => `ACC-${Date.now()}`}
          successMessage={createSuccessMessage}
          errorMessage={createErrorMessage}
          consentSuccessData={consentSuccessData}
          onResetSuccess={handleResetConsentSuccess}
          assistedJourneyFlags={currentAssistedJourneyFlags}
        />
      </DrawerProvider>

      <DrawerProvider
        side="right"
        open={qrDrawerOpen}
        onOpenChange={setQrDrawerOpen}
      >
        <GenerateQRDrawer
          isOpen={qrDrawerOpen}
          onClose={() => setQrDrawerOpen(false)}
          products={productOptions}
          multiConsentLimit={multiConsentLimit}
          allowMultiConsent={allowMultiConsent}
          qrBaseUrl={staticQrBaseUrl}
        />
      </DrawerProvider>

      <DrawerProvider
        side="right"
        open={fiDataDrawerOpen}
        onOpenChange={setFiDataDrawerOpen}
      >
        <FiDataDrawer
          isOpen={fiDataDrawerOpen}
          onClose={() => setFiDataDrawerOpen(false)}
          consent={fiDataConsent}
          analyticsOptions={analyticsOptions}
          onDownloadAnalytics={handleDownloadAnalytics}
          downloadAccountWiseXml={Boolean(userUiConfig?.download_account_wise_xml)}
        />
      </DrawerProvider>

      <DrawerProvider
        side="right"
        open={accountDetailsDrawerOpen}
        onOpenChange={setAccountDetailsDrawerOpen}
      >
        <AccountDetailsDrawer
          isOpen={accountDetailsDrawerOpen}
          onClose={() => setAccountDetailsDrawerOpen(false)}
          consent={accountDetailsConsent}
        />
      </DrawerProvider>

      {/* CSV Download Modal */}
      <DrawerProvider
        side="right"
        open={csvModalOpen}
        onOpenChange={setCsvModalOpen}
      >
        {csvModalOpen && (
          <CsvDownloadModal
            onClose={() => setCsvModalOpen(false)}
            onDownload={handleCsvDownload}
            isDownloading={csvDownloading}
            isJobBasedDownload={isJobBasedDownloadEnabled}
          />
        )}
      </DrawerProvider>

      {/* Lending Report Drawer */}
      <DrawerProvider
        side="right"
        open={lendingReportOpen}
        onOpenChange={setLendingReportOpen}
      >
        {lendingReportOpen && (
          <LendingReportDrawer
            isOpen={lendingReportOpen}
            onClose={() => setLendingReportOpen(false)}
            consentId={lendingReportConsentId}
            consentHandle={lendingReportConsentHandle}
          />
        )}
      </DrawerProvider>
    </DrawerProvider>
  );
};
